import { gobrainLiveConfig, publicDataDomain, qplayerRestrictedAccounts } from "../../util/config";
import { EDITOR_PLAYER_ID } from "../../util/util";

export namespace PlayerHelper {
    export interface PlayerProps {
        playerId: string;
        playerMedia: string;
        mediaType?: string;
        token?: string;
        accountId?: string;
        playlistId?: string;
        classNames?: string;
        isMediaLive?: boolean;
        isVisible?: boolean;
        isMaintainProportions?: boolean;
        endOfScene?: any;
        settings?: any;
        queryParams?: any;
        isStoryPlaylist?: boolean;
        handlePlayerHover?: () => void;
        layout: "desktop" | "mobile";
    }

    export const createPlayerInstance = ({ playerProps, scenario, onPlayerReady, mediaType }: { playerProps: Omit<PlayerProps, "i18n">; mediaType: string; scenario?: any; onPlayerReady?: (_isReady: boolean) => void }) => {
        const {
            playerId,
            isVisible,
            endOfScene,
            token,
            playerMedia,
            settings,
            accountId,
            playlistId,
            queryParams,
            isMediaLive,
            isStoryPlaylist,
        } = playerProps;

        if (!accountId) {
            return;
        }

        const {
            autoplay: paramAutoplay,
            mute: paramMute,
            repeat: paramRepeat,
            ignoreAnalytics: paramIgnoreAnalytics,
            language: paramLanguage,
            analyticsCookies: paramAnalyticsCookies,
            subtitlesAuto: paramSubtitlesAuto
        } = queryParams ?? {};

        const defaultVolume = settings.playbackOptions?.setDefaultVolume ? settings.playbackOptions?.defaultVolumeValue / 100 || 0.5 : 0.5;
        let volume =
            !isVisible || playerId === EDITOR_PLAYER_ID
                ? 0
                : !paramMute
                    ? settings.playbackOptions?.muteAtStart
                        ? 0
                        : defaultVolume
                    : paramMute === 'true'
                        ? 0
                        : defaultVolume;
            // Minimized widget should always be muted at start
            volume = queryParams?.fromQplayerWidget === 'true' ? 0 : volume;
        const autoplay =
            playerId === EDITOR_PLAYER_ID
                ? true
                : !isVisible
                    ? false
                    : !paramAutoplay
                        ? settings.playbackOptions?.autostart
                            ? true
                            : false
                        : paramAutoplay === 'true';
        const repeat =
            mediaType === 'scenario'
                ? endOfScene?.type === 'loopCurrentScene'
                : !paramRepeat
                    ? settings.playbackOptions?.replay
                        ? true
                        : false
                    : paramRepeat === 'true'
                        ? true
                        : false;

        const moduleSettings = {
            TopControls: { download: { enabled: true }, sharing: { enabled: true } },
            MobileControls: { download: { enabled: true }, sharing: { enabled: true } },
            Data: token
                ? {
                        headers: {
                            'X-Authorization': 'AVP1 ' + token,
                        },
                    }
                : {},
            Poster: {
                settings: {
                    // cover being handled on qplayer
                    supportsCovers: false,
                    showPoster: false,
                },
            },
            ChromeCast: {
                name: 'ChromeCast',
                type: 'ChromeCast',
                settings: {
                    supportsChromeCast: settings.bottomControls?.showCastDevices,
                },
            },
            Advertisement: {
                name: 'Advertisement',
                type: 'Advertisement',
                settings: {
                    enabled: settings?.advertisementSettings?.enabled,
                    advertisementServer: settings?.advertisementSettings?.advertisementServer,
                    cTAClickArea: settings?.advertisementSettings?.cTAClickArea,
                },
            },
        };

        let data = publicDataDomain + accountId + '/medias/' + playerMedia;
        if (playlistId && !isStoryPlaylist) {
            data = publicDataDomain + accountId + '/playlists/' + playlistId + '/medias';
        }

        const ignoreAnalytics =
            playerId === EDITOR_PLAYER_ID ||
            (paramIgnoreAnalytics ? paramIgnoreAnalytics === 'true' : settings?.additionalSettings?.disableAnalytics === true);
        const language = paramLanguage ? paramLanguage : settings?.accessibilitySettings?.language;

        const defaultSceneBGMediaId = scenario?.defaultSceneBGMediaId ?? "";
        const isDefaultSceneBGMedia = playerMedia === defaultSceneBGMediaId && mediaType === 'scenario';

        const analyticsCookies = paramAnalyticsCookies ? paramAnalyticsCookies === 'true' : !(settings?.additionalSettings?.noCookies === true);

        const embedSettings = {
            config: gobrainLiveConfig,
            data: data,
            widgetId: playerId,
            volume: volume,
            autoplay: isMediaLive && !isDefaultSceneBGMedia ? true : autoplay,
            repeat: repeat,
            language: language === 'dk' ? 'da' : language,
            ignoreAnalytics: ignoreAnalytics,
            moduleSettings: moduleSettings,
            subtitlesAutoOn: !paramSubtitlesAuto ? settings?.playbackOptions?.subtitlesAutoOn : paramSubtitlesAuto === 'true',
            noSchemaData: settings?.additionalSettings?.noSchemaData,
            useCookie: analyticsCookies
        };

        (window as any).GoBrain.create(document.getElementById(`${playerId}-container`), embedSettings).on('ready', () => {
            onPlayerReady?.(true);
            if (isVisible || playerId === EDITOR_PLAYER_ID || mediaType === 'video') {
                return;
            }

            if (!(qplayerRestrictedAccounts ?? "").includes(accountId)) {
                setTimeout(() => {
                    const widget = (window as any).GoBrain.widgets(playerId);
                    widget && widget.preload && widget.preload();
                });
            }
        });
    }

    interface PlayerEvents {
            queueItem: (_item: any) => void;
            playable: () => void;
            play: () => void;
            pause: () => void;
            playComplete: () => void;
            position: (_position: number) => void;
            duration: (_duration: number) => void;
            notification: (_event: any) => void;
            volume: (_volume: any) => void;
            error: (_error: any) => void;
            adsStart: () => void;
            adsPlay: () => void;
            adsSkipped: () => void;
            adsComplete: () => void;
            adsAllComplete: () => void;
    }
	export const subscribePlayerEvents = (playerId: string, playerEvents: PlayerEvents) => {
		if ((window as any).GoBrain.widgets()[playerId]) {
            Object.keys(playerEvents).forEach((event) => {
                (window as any).GoBrain.widgets()[playerId].on(event, (playerEvents as any)[event]);
            });

            const gobrainVolumeController = document.querySelector('.gobrain-controller-volume') as any;
            if (gobrainVolumeController) {
                gobrainVolumeController.style = '';
            }
		}
	};

	export const unsubscribePlayerEvents = (playerId: string, events: PlayerEvents) => {
		if ((window as any).GoBrain.widgets()[playerId]) {
            Object.keys(events).forEach((event) => {
                (window as any).GoBrain.widgets()[playerId].off(event, (events as any)[event]);
            });
		}
	};

	export const movePlayerToPosition = (playerId: string, positionToMove: number) => {
		(window as any).GoBrain.widgets(playerId)?.position?.(positionToMove);
	};

    export const handleButtonForward = (playerId: string, currentPosition: number | "") => {
		let forwardPosition = (currentPosition === '' ? 0 : currentPosition) + 15;
		(window as any).GoBrain.widgets(playerId)?.position?.(forwardPosition);
	};

	export const handleButtonReward = (playerId: string, currentPosition: number | "") => {
		let rewardPosition = currentPosition === '' || currentPosition < 15 ? 0 : currentPosition - 15;
		(window as any).GoBrain.widgets(playerId)?.position?.(rewardPosition);
	};

    export const onReceiveMessage = ({event, playerId, isVisible, currentPosition }: { event: any, playerId: string, isVisible: boolean, currentPosition: number | '' }) => {

        if (!isVisible) {
            return;
        }

        if (event.data?.type === 'move-position-from-editor' && event.data?.position !== undefined && event.data?.position !== null) {
            movePlayerToPosition(playerId, event.data?.position);
        }

        if (event.data?.type === 'pause') {
            (window as any).GoBrain.widgets(playerId)?.pause?.();
        }

        if (event.data?.type === 'play') {
            (window as any).GoBrain.widgets(playerId)?.play?.();
        }

        if (event.data?.type === 'forward') {
            handleButtonForward(playerId, currentPosition);
        }

        if (event.data?.type === 'reward') {
            handleButtonReward(playerId, currentPosition);
        }

        if (event.data?.type === 'mute') {
            (window as any).GoBrain.widgets(playerId)?.volume?.(0);
        }

        if (event.data?.type === 'unMute') {
            (window as any).GoBrain.widgets(playerId)?.volume?.(0.5);
        }
    }

	export const setThumbnailForPlayer = (thumbnailUrl: string, setLoading: (_value: boolean) => void) => {
		if (!thumbnailUrl) {
            setLoading(false);
			return;
		}

		[3, 5, 7, 10].map((i) => {
			setTimeout(() => {
				const gobrainPoster = document.querySelector('.gobrain-poster') as any;
				if (gobrainPoster) {
					gobrainPoster.style.display = 'none';
					gobrainPoster.style.backgroundImage = `url(${thumbnailUrl})`;
				}

				if (i === 10) {
                    setLoading(false);
                    if (gobrainPoster) {
                        gobrainPoster.style.display = '';
                    }
				}
			}, i * 100);
		});
	};
}